import { Avatar, AvatarImage } from '@components/ui/avatar';
import { Skeleton } from '@components/ui/skeleton';
import type { ProcessModel } from '@models/process.model';
import type { WorkflowModel } from '@models/workflow.model';

import { getTimeAgo } from '@pages/Home/processCard/processCard.utils';
import {
  formatElapsedTime,
  generationStatusToProgress,
  MAX_NAME_LENGTH
} from '@pages/Settings/generation';
import { GenerationStatus } from '@stratumn/studio-shared';
import { motion } from 'framer-motion';

import { useGenerations } from '@/hooks/queries/generations/useGenerations';

import {
  LastEditInfo,
  Side,
  WorkflowCardContainer,
  WorkflowInfos,
  WorkflowName,
  WorkflowPreview,
  WorkflowStatus,
  workflowCardClassName
} from '../process.style';
import BpmnPreview from './bpmnPreview';

type WorkflowCardProps = {
  workflow: WorkflowModel;
  process: ProcessModel;
  loading?: boolean;
};

export const WorkflowCard = ({
  workflow,
  process,
  loading = false
}: WorkflowCardProps) => {
  const { generations } = useGenerations({
    processId: process.id,
    take: 1,
    withCount: false
  });
  const lastGeneration = generations[0];
  const editorPath = `/${process.id}/${workflow.id}/editor`;
  const avatar = lastGeneration?.createdBy.avatar;

  return (
    <WorkflowCardContainer to={loading ? '' : editorPath}>
      <WorkflowPreview>
        <BpmnPreview xml={workflow.workflowXML} />
      </WorkflowPreview>

      <WorkflowInfos>
        <Side className="truncate">
          <WorkflowName>{workflow.name}</WorkflowName>

          {lastGeneration && (
            <div className="flex flex-col">
              <LastEditInfo>
                last updated {getTimeAgo(workflow.updatedAt)}
              </LastEditInfo>{' '}
              <LastEditInfo className="flex">
                by {lastGeneration.createdBy.name.slice(0, MAX_NAME_LENGTH)}{' '}
                <Avatar className="ml-2 h-5 w-5">
                  <AvatarImage
                    src={
                      avatar ?? 'https://www.w3schools.com/howto/img_avatar.png'
                    }
                  />
                </Avatar>
              </LastEditInfo>
            </div>
          )}
        </Side>
        <Side className="items-end text-right">
          <WorkflowStatus>
            {lastGeneration
              ? generationStatusToProgress(lastGeneration.status, 16)
              : generationStatusToProgress(GenerationStatus.PENDING, 16)}
          </WorkflowStatus>
          {lastGeneration && (
            <LastEditInfo>
              generated {formatElapsedTime(lastGeneration.createdAt)}{' '}
            </LastEditInfo>
          )}
        </Side>
      </WorkflowInfos>
    </WorkflowCardContainer>
  );
};

type SkeletonProps = {
  index: number;
};
export const WorkflowCardSkeleton = ({ index }: SkeletonProps) => (
  <motion.div
    animate={{ opacity: 1, y: 0 }}
    className={workflowCardClassName}
    initial={{ opacity: 0, y: 10 }}
    transition={{ duration: 0.3, delay: index * 0.1 }}
  >
    <WorkflowPreview>
      <Skeleton className="h-full w-full rounded-none" />
    </WorkflowPreview>
    <WorkflowInfos className="shrink">
      <Side className="gap-1">
        <Skeleton className="h-5 w-32" />
        <div className="mt-2 flex flex-col gap-1">
          <Skeleton className="h-4 w-40" />
          <div className="flex items-center">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="ml-2 size-4 rounded-full" />
          </div>
        </div>
      </Side>
      <Side className="items-end text-right">
        <Skeleton className="size-4 rounded-full" />
        <Skeleton className="mt-2 h-3 w-28" />
      </Side>
    </WorkflowInfos>
  </motion.div>
);
